import {
  Box,
  Typography,
  Container,
  useTheme,
  Button,
  Card,
  CardContent,
  Stack,
  IconButton,
  Tooltip,
  Zoom,
  useScrollTrigger,
  Fab,
  Chip,
  Skeleton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import WorkIcon from '@mui/icons-material/Work';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupsIcon from '@mui/icons-material/Groups';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ConnectWithoutContactIcon from '@mui/icons-material/ConnectWithoutContact';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import { AuthContext } from "../context/AuthContext";
import { useContext, useState, useEffect, useMemo } from "react";
import { hasCompletedPersonalInfo } from '../utils/profileValidation';
import { createAxiosInstance } from '../api/axios';


const ScrollTop = ({ children }) => {
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Zoom in={trigger}>
      <Box
        onClick={handleClick}
        role="presentation"
        sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1000 }}
      >
        {children}
      </Box>
    </Zoom>
  );
};

const Home = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [landingStats, setLandingStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const axios = useMemo(() => createAxiosInstance(), []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    const fetchLandingStats = async () => {
      try {
        setStatsLoading(true);
        const response = await axios.get('/landing-stats/');
        setLandingStats(response.data);
      } catch (error) {
        console.error('Error fetching landing stats:', error);
        // Set default values on error
        setLandingStats({
          job_count: 0,
          application_count: 0,
          user_count: 0
        });
      } finally {
        setStatsLoading(false);
      }
    };

    fetchLandingStats();
  }, [axios]);

  useEffect(() => {
    if (user && !hasCompletedPersonalInfo(user)) {
      navigate('/profile', { state: { showCompletenessToast: true } });
    }
  }, [user, navigate]);

  // if(user && !hasCompletedPersonalInfo(user)) {
  //   return <PersonalInfoForm/>
  // }

  const features = [
    {
      icon: AssessmentIcon,
      title: "Resume Analysis",
      description: "Get instant AI-powered resume evaluation and recommendations",
      color: '#3b82f6',
    },
    {
      icon: ConnectWithoutContactIcon,
      title: "Global Matching",
      description: "Connect with top employers from around the world",
      color: '#3b82f6',
    },
    {
      icon: RocketLaunchIcon,
      title: "Fast Application",
      description: "Apply to multiple opportunities in seconds with one profile",
      color: '#3b82f6',
    },
    {
      icon: TrendingUpIcon,
      title: "Career Growth",
      description: "Track your progress and improve your marketability",
      color: '#3b82f6',
    },
  ];

  const categories = [
    {
      title: "Software Development",
      description: "Full-stack, backend, frontend, and mobile development roles",
      icon: WorkIcon,
      opportunities: "850+",
      color: 'rgba(59, 130, 246, 0.1)',
    },
    {
      title: "Data & AI",
      description: "Machine learning, data science, and AI engineering positions",
      icon: AutoAwesomeIcon,
      opportunities: "450+",
      color: 'rgba(59, 130, 246, 0.1)',
    },
    {
      title: "Design & Creative",
      description: "UI/UX, graphic design, and creative roles worldwide",
      icon: BusinessCenterIcon,
      opportunities: "320+",
      color: 'rgba(59, 130, 246, 0.1)',
    },
    {
      title: "Business & Marketing",
      description: "Growth, marketing, sales, and business development",
      icon: GroupsIcon,
      opportunities: "580+",
      color: 'rgba(59, 130, 246, 0.1)',
    },
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundColor: '#ffffff',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Animated Background Elements */}
      <Box sx={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 0,
      }}>
        <Box
          sx={{
            position: 'absolute',
            width: '384px',
            height: '384px',
            background: 'radial-gradient(circle, rgba(59, 130, 246, 0.05) 0%, transparent 70%)',
            borderRadius: '50%',
            filter: 'blur(40px)',
            left: '10%',
            top: '20%',
            transform: `translate(${mousePosition.x * 0.02}px, ${mousePosition.y * 0.02}px)`,
            transition: 'transform 0.3s ease-out',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: '256px',
            height: '256px',
            background: 'radial-gradient(circle, rgba(59, 130, 246, 0.03) 0%, transparent 70%)',
            borderRadius: '50%',
            filter: 'blur(30px)',
            right: '15%',
            bottom: '30%',
            transform: `translate(${mousePosition.x * -0.01}px, ${mousePosition.y * -0.01}px)`,
            transition: 'transform 0.3s ease-out',
          }}
        />
      </Box>

      {/* Hero Section */}
      <Box sx={{ 
        position: 'relative',
        pt: { xs: 12, md: 16 },
        pb: { xs: 8, md: 12 },
        px: 2,
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', maxWidth: '1100px', mx: 'auto' }}>
            {/* New Badge */}
            <Zoom in timeout={500}>
              <Chip
                icon={<AutoAwesomeIcon sx={{ fontSize: '16px !important' }} />}
                label="AI-Powered Talent Matching Platform"
                sx={{
                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                  color: '#3b82f6',
                  fontWeight: 600,
                  fontSize: '14px',
                  mb: 3,
                  px: 2,
                  py: 2.5,
                  '&:hover': {
                    backgroundColor: 'rgba(59, 130, 246, 0.15)',
                  },
                }}
              />
            </Zoom>
            
            {/* Main Heading */}
            <Typography
              component="h1"
              sx={{
                fontSize: { xs: '3rem', md: '3.75rem' },
                fontWeight: 'bold',
                mb: 4,
                lineHeight: 1.2,
                color: '#1f2937',
                textAlign: 'center',
                textWrap: 'balance',
                opacity: 1,
                transform: 'none',
              }}
            >
              Your Gateway to<br />the Future of Work
            </Typography>

            {/* Subtitle */}
            <Typography
              sx={{
                fontSize: { xs: '1.125rem', md: '1.375rem' },
                mb: 3,
                color: '#1f2937',
                lineHeight: 1.6,
                maxWidth: '52rem',
                mx: 'auto',
                fontWeight: 500,
              }}
            >
              Connect with AI labs and global companies hiring top talent<br />across engineering, STEM, design, media, and more.
            </Typography>

            {/* Supporting Line */}
            <Typography
              sx={{
                fontSize: { xs: '1.25rem', md: '1.5rem' },
                mb: 6,
                color: '#6b7280',
                lineHeight: 1.6,
                maxWidth: '48rem',
                mx: 'auto',
                fontWeight: 400,
                fontStyle: 'italic',
              }}
            >
              Priyo finds the perfect remote job. You focus on excellence.
            </Typography>

            {/* CTA Buttons */}
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={2}
              justifyContent="center"
              sx={{ mb: 8 }}
            >
              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  if (user) {
                    navigate('/dashboard');
                  } else {
                    navigate('/auth/sign-up?redirect=/dashboard');
                  }
                }}
                sx={{
                  backgroundColor: '#3b82f6',
                  color: '#ffffff',
                  fontWeight: 400,
                  fontSize: { xs: '1rem', md: '1.125rem' },
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  boxShadow: '0 4px 14px 0 rgba(59, 130, 246, 0.39)',
                  '&:hover': {
                    backgroundColor: '#2563eb',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(59, 130, 246, 0.4)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Upload Your Resume Now
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/opportunities')}
                sx={{
                  borderColor: '#3b82f6',
                  color: '#3b82f6',
                  fontWeight: 400,
                  fontSize: { xs: '1rem', md: '1.125rem' },
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                    borderColor: '#2563eb',
                    backgroundColor: 'rgba(59, 130, 246, 0.04)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Browse Opportunities
              </Button>
            </Stack>

            {/* Stats */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 4,
                maxWidth: '32rem',
                mx: 'auto',
              }}
            >
              {/* Opportunities Stat */}
              <Box sx={{ textAlign: 'center' }}>
                {statsLoading ? (
                  <Skeleton 
                    variant="text" 
                    sx={{ 
                      fontSize: { xs: '2.25rem', md: '3rem' },
                      mb: 1,
                      mx: 'auto',
                      width: '80%',
                    }} 
                  />
                ) : (
                  <Typography
                    sx={{
                      fontSize: { xs: '2.25rem', md: '3rem' },
                      fontWeight: 900,
                      color: '#1f2937',
                      mb: 1,
                    }}
                  >
                    {landingStats?.job_count.toLocaleString()}+
                  </Typography>
                )}
                <Typography
                  sx={{
                    color: '#6b7280',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                  }}
                >
                  Opportunities
                </Typography>
              </Box>

              {/* Applications Stat */}
              {/* <Box sx={{ textAlign: 'center' }}>
                {statsLoading ? (
                  <Skeleton 
                    variant="text" 
                    sx={{ 
                      fontSize: { xs: '2.25rem', md: '3rem' },
                      mb: 1,
                      mx: 'auto',
                      width: '80%',
                    }} 
                  />
                ) : (
                  <Typography
                    sx={{
                      fontSize: { xs: '2.25rem', md: '3rem' },
                      fontWeight: 900,
                      color: '#1f2937',
                      mb: 1,
                    }}
                  >
                    {landingStats?.application_count.toLocaleString()}+
                  </Typography>
                )}
                <Typography
                  sx={{
                    color: '#6b7280',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                  }}
                >
                  Applications
                </Typography>
              </Box> */}

              {/* Talents Registered Stat */}
              <Box sx={{ textAlign: 'center' }}>
                {statsLoading ? (
                  <Skeleton 
                    variant="text" 
                    sx={{ 
                      fontSize: { xs: '2.25rem', md: '3rem' },
                      mb: 1,
                      mx: 'auto',
                      width: '80%',
                    }} 
                  />
                ) : (
                  <Typography
                    sx={{
                      fontSize: { xs: '2.25rem', md: '3rem' },
                      fontWeight: 900,
                      color: '#1f2937',
                      mb: 1,
                    }}
                  >
                    {landingStats?.user_count.toLocaleString()}+
                  </Typography>
                )}
                <Typography
                  sx={{
                    color: '#6b7280',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                  }}
                >
                  Talents Registered
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Category Categories Section */}
      <Box sx={{ 
        py: { xs: 8, md: 12 },
        backgroundColor: 'rgba(249, 250, 251, 0.5)',
        position: 'relative',
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              sx={{
                fontSize: { xs: '2rem', md: '2.5rem' },
                fontWeight: 700,
                color: '#1f2937',
                mb: 2,
              }}
            >
              Explore Opportunity Categories
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '1rem', md: '1.125rem' },
                color: '#6b7280',
                maxWidth: '600px',
                mx: 'auto',
              }}
            >
              Choose from our carefully curated selection of opportunities across different industries and roles
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
              gap: 3,
            }}
          >
            {categories.map((category, index) => (
              <Card
                key={index}
                onClick={() => navigate('/opportunities')}
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                  border: '1px solid #e5e7eb',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    '& .category-icon': {
                      transform: 'scale(1.1) rotate(5deg)',
                    },
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(59, 130, 246, 0.02) 100%)',
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                    '.MuiCard-root:hover &': {
                      opacity: 1,
                    },
                  }}
                />
                <CardContent sx={{ p: 3, position: 'relative', zIndex: 1, textAlign: 'center' }}>
                  <Box
                    className="category-icon"
                    sx={{
                      width: '64px',
                      height: '64px',
                      borderRadius: '16px',
                      backgroundColor: category.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <category.icon sx={{ fontSize: 32, color: '#3b82f6' }} />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '1.25rem',
                      fontWeight: 600,
                      color: '#1f2937',
                      mb: 1,
                    }}
                  >
                    {category.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      color: '#6b7280',
                      mb: 2,
                      lineHeight: 1.5,
                    }}
                  >
                    {category.description}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, fontSize: '0.75rem', color: '#9ca3af' }}>
                    <span>{category.opportunities} opportunities</span>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate('/opportunities')}
                    sx={{
                      mt: 2,
                      width: '100%',
                      borderColor: '#e5e7eb',
                      color: '#6b7280',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        borderColor: '#3b82f6',
                        backgroundColor: 'rgba(59, 130, 246, 0.04)',
                        color: '#3b82f6',
                      },
                    }}
                  >
                    Explore Opportunities
                  </Button>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ 
        py: { xs: 8, md: 12 },
        px: 2,
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              sx={{
                fontSize: { xs: '2rem', md: '2.5rem' },
                fontWeight: 700,
                color: '#1f2937',
                mb: 2,
              }}
            >
              Why Choose Priyo Work?
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '1rem', md: '1.125rem' },
                color: '#6b7280',
                maxWidth: '600px',
                mx: 'auto',
              }}
            >
              We provide a comprehensive career platform with modern tools and expert guidance
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
              gap: 4,
            }}
          >
            {features.map((feature, index) => (
              <Box
                key={index}
                sx={{
                  textAlign: 'center',
                  '&:hover .feature-icon': {
                    transform: 'scale(1.1)',
                    backgroundColor: '#3b82f6',
                    color: '#ffffff',
                  },
                }}
              >
                <Box
                  className="feature-icon"
                  sx={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    color: '#3b82f6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                    transition: 'all 0.3s ease',
                  }}
                >
                  <feature.icon sx={{ fontSize: 32 }} />
                </Box>
                <Typography
                  sx={{
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    color: '#1f2937',
                    mb: 1,
                  }}
                >
                  {feature.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: '#6b7280',
                    lineHeight: 1.6,
                  }}
                >
                  {feature.description}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: '#3b82f6',
        color: '#ffffff',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0.1,
            backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
            backgroundSize: '50px 50px',
            animation: 'backgroundMove 20s linear infinite',
            '@keyframes backgroundMove': {
              '0%': { backgroundPosition: '0% 0%' },
              '100%': { backgroundPosition: '100% 100%' },
            },
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <Typography
            sx={{
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 700,
              mb: 2,
            }}
          >
            Ready to Start Your Career Journey?
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '1.125rem', md: '1.25rem' },
              mb: 4,
              opacity: 0.9,
              maxWidth: '600px',
              mx: 'auto',
            }}
          >
            Join thousands of talents who are already advancing their careers with Priyo Work
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => {
              if (user) {
                navigate('/dashboard');
              } else {
                navigate('/auth/sign-up?redirect=/dashboard');
              }
            }}
            sx={{
              backgroundColor: '#ffffff',
              color: '#3b82f6',
              fontWeight: 600,
              fontSize: { xs: '1rem', md: '1.125rem' },
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
              '&:hover': {
                backgroundColor: '#f9fafb',
                transform: 'translateY(-2px)',
                boxShadow: '0 15px 35px rgba(0, 0, 0, 0.25)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            Get Started Now
          </Button>

          {/* <Box sx={{ mt: 6 }}>
            <Typography sx={{ fontSize: '0.875rem', opacity: 0.8, mb: 2 }}>
              Follow us on social media
            </Typography>
            <Stack 
              direction="row" 
              spacing={2}
              justifyContent="center"
            >
              <Tooltip title="Follow us on Facebook">
                <IconButton
                  component="a"
                  href="https://www.facebook.com/priyolife/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                    width: 48,
                    height: 48,
                  }}
                >
                  <FacebookIcon sx={{ fontSize: 24 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Follow us on LinkedIn">
                <IconButton
                  component="a"
                  href="https://www.linkedin.com/company/priyo-work/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                    width: 48,
                    height: 48,
                  }}
                >
                  <LinkedInIcon sx={{ fontSize: 24 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box> */}
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{
        backgroundColor: '#ffffff',
        color: '#1f2937',
        borderTop: '1px solid #e5e7eb',
      }}>
        <Container maxWidth="lg" sx={{ py: 6 }}>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { 
              xs: '1fr', 
              sm: 'repeat(2, 1fr)', 
              md: 'repeat(6, 1fr)' 
            },
            gap: 4,
          }}>
            {/* Logo and Location */}
            <Box sx={{ gridColumn: { xs: '1', md: 'span 2' } }}>
              <Box sx={{ mb: 3, px: 0.5 }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 700, 
                  mb: 2,
                  color: '#1f2937',
                }}>
                  San Francisco, CA
                </Typography>
              </Box>
              
              {/* Social Media Icons */}
              <Stack direction="row" spacing={2}>
                <Tooltip title="LinkedIn">
                  <IconButton
                    component="a"
                    href="https://www.linkedin.com/company/priyo-work/"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: '#f3f4f6',
                      color: '#6b7280',
                      '&:hover': {
                        backgroundColor: '#3b82f6',
                        color: '#ffffff',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <LinkedInIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Facebook">
                  <IconButton
                    component="a"
                    href="https://www.facebook.com/priyolife/"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: '#f3f4f6',
                      color: '#6b7280',
                      '&:hover': {
                        backgroundColor: '#3b82f6',
                        color: '#ffffff',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <FacebookIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>

            {/* For Companies */}
            <Box>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: 600, 
                mb: 2,
                color: '#1f2937',
              }}>
                For Companies
              </Typography>
              <Stack spacing={1}>
                <Typography
                  component="a"
                  href="mailto:work@priyo.com"
                  sx={{
                    color: '#6b7280',
                    fontSize: '0.875rem',
                    textDecoration: 'none',
                    '&:hover': {
                      color: '#3b82f6',
                    },
                    transition: 'color 0.3s ease',
                  }}
                >
                  Get in Touch
                </Typography>
              </Stack>
            </Box>

            {/* For Talents */}
            <Box>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: 600, 
                mb: 2,
                color: '#1f2937',
              }}>
                For Talents
              </Typography>
              <Stack spacing={1}>
                <Typography
                  component="a"
                  href="/opportunities"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/opportunities');
                  }}
                  sx={{
                    color: '#6b7280',
                    fontSize: '0.875rem',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    '&:hover': {
                      color: '#3b82f6',
                    },
                    transition: 'color 0.3s ease',
                  }}
                >
                  Apply
                </Typography>
                <Typography
                  component="a"
                  href="/opportunities"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/opportunities');
                  }}
                  sx={{
                    color: '#6b7280',
                    fontSize: '0.875rem',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    '&:hover': {
                      color: '#3b82f6',
                    },
                    transition: 'color 0.3s ease',
                  }}
                >
                  Opportunities
                </Typography>
              </Stack>
            </Box>

            {/* Resources */}
            <Box>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: 600, 
                mb: 2,
                color: '#1f2937',
              }}>
                Resources
              </Typography>
              <Stack spacing={1}>
                <Typography
                  sx={{
                    color: '#6b7280',
                    fontSize: '0.875rem',
                  }}
                >
                  Careers
                </Typography>
                <Typography
                  sx={{
                    color: '#6b7280',
                    fontSize: '0.875rem',
                  }}
                >
                  Blog
                </Typography>
              </Stack>
            </Box>

            {/* Support */}
            <Box>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: 600, 
                mb: 2,
                color: '#1f2937',
              }}>
                Support
              </Typography>
              <Stack spacing={1}>
                <Typography
                  component="a"
                  href="mailto:work@priyo.com"
                  sx={{
                    color: '#6b7280',
                    fontSize: '0.875rem',
                    textDecoration: 'none',
                    '&:hover': {
                      color: '#3b82f6',
                    },
                    transition: 'color 0.3s ease',
                  }}
                >
                  work@priyo.com
                </Typography>
              </Stack>
            </Box>
          </Box>

          {/* Copyright */}
          <Box sx={{ 
            borderTop: '1px solid #e5e7eb', 
            mt: 4, 
            pt: 4, 
            textAlign: 'center' 
          }}>
            <Typography sx={{ 
              color: '#6b7280', 
              fontSize: '0.875rem' 
            }}>
              © {new Date().getFullYear()} Priyo Work. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>

    
      <ScrollTop>
        <Fab 
          color="primary" 
          size="small" 
          aria-label="scroll back to top"
          sx={{
            backgroundColor: theme.palette.primary.main,
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
              transform: 'translateY(-2px)',
            },
            transition: 'all 0.3s ease'
          }}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      </ScrollTop>
    </Box>
  );
};

export default Home;
